/**
 * Reports dashboard — `GET reports/dashboard/`. The response shape branches
 * by role server-side (reports/routes.ts:153-303): a TENANT gets a small
 * `active_lease`/`next_payment`/`maintenance_requests` summary, every manager
 * role gets a much larger properties/units/leases/payments/maintenance/
 * expenses object. `isTenantDashboard` narrows which shape came back — this
 * is a real conditional render, not one shape assumed for every caller.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { getDashboard } from '../../api/endpoints/reports';
import { isTenantDashboard } from '../../types/report';

export function ReportsDashboardPage(): React.JSX.Element {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['reports-dashboard'],
    queryFn: () => getDashboard(),
  });

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const dashboard = query.data;

  return (
    <section>
      <h2>Reports</h2>

      {!isTenantDashboard(dashboard) && (
        <p>
          <Link to="rent-roll">Rent roll</Link> <Link to="maintenance">Maintenance report</Link>{' '}
          <Link to="financial">Financial report</Link> <Link to="tax-summary">Tax summary</Link>
        </p>
      )}

      {isTenantDashboard(dashboard) ? (
        <>
          <h3>Your lease</h3>
          {dashboard.active_lease === null ? (
            <p>No active lease.</p>
          ) : (
            <dl>
              <dt>Property</dt>
              <dd>{dashboard.active_lease.property}</dd>
              <dt>Unit</dt>
              <dd>{dashboard.active_lease.unit}</dd>
              <dt>Rent</dt>
              <dd>{dashboard.active_lease.rent_amount}</dd>
              <dt>Lease ends</dt>
              <dd>{dashboard.active_lease.end_date}</dd>
            </dl>
          )}

          <h3>Next payment</h3>
          {dashboard.next_payment === null ? (
            <p>Nothing due.</p>
          ) : (
            <dl>
              <dt>Amount</dt>
              <dd>{dashboard.next_payment.amount}</dd>
              <dt>Due date</dt>
              <dd>{dashboard.next_payment.due_date}</dd>
              <dt>Status</dt>
              <dd>{dashboard.next_payment.status}</dd>
            </dl>
          )}

          <h3>Maintenance requests</h3>
          <dl>
            <dt>Total</dt>
            <dd>{dashboard.maintenance_requests.total}</dd>
            <dt>New</dt>
            <dd>{dashboard.maintenance_requests.new}</dd>
            <dt>In progress</dt>
            <dd>{dashboard.maintenance_requests.in_progress}</dd>
          </dl>
        </>
      ) : (
        <>
          <h3>Properties</h3>
          <dl>
            <dt>Total</dt>
            <dd>{dashboard.properties.total}</dd>
            {user?.role === 'SUPERAGENT' && (
              <>
                <dt>Pending approval</dt>
                <dd>{dashboard.properties.pending_approval}</dd>
              </>
            )}
          </dl>

          <h3>Units</h3>
          <dl>
            <dt>Total</dt>
            <dd>{dashboard.units.total}</dd>
            <dt>Vacant</dt>
            <dd>{dashboard.units.vacant}</dd>
            <dt>Occupied</dt>
            <dd>{dashboard.units.occupied}</dd>
          </dl>

          <h3>Leases</h3>
          <dl>
            <dt>Total</dt>
            <dd>{dashboard.leases.total}</dd>
            <dt>Active</dt>
            <dd>{dashboard.leases.active}</dd>
            <dt>Expiring soon</dt>
            <dd>{dashboard.leases.expiring_soon}</dd>
          </dl>

          <h3>Payments</h3>
          <dl>
            <dt>Total due</dt>
            <dd>{dashboard.payments.total_due}</dd>
            <dt>Overdue count</dt>
            <dd>{dashboard.payments.overdue}</dd>
            <dt>Overdue amount</dt>
            <dd>{dashboard.payments.overdue_amount}</dd>
          </dl>

          <h3>Maintenance</h3>
          <dl>
            <dt>Total</dt>
            <dd>{dashboard.maintenance.total}</dd>
            <dt>New</dt>
            <dd>{dashboard.maintenance.new}</dd>
            <dt>In progress</dt>
            <dd>{dashboard.maintenance.in_progress}</dd>
            <dt>Urgent</dt>
            <dd>{dashboard.maintenance.urgent}</dd>
          </dl>

          <h3>Expenses</h3>
          <dl>
            <dt>This month</dt>
            <dd>{dashboard.expenses.this_month}</dd>
          </dl>
        </>
      )}
    </section>
  );
}

export default ReportsDashboardPage;
